import { MapPin, X as CloseIcon } from "lucide-react";

function ServiceRequestModal({
  isOpen,
  isLoading,
  // The reason a load failed is toasted by the parent; the modal only needs to
  // know that it has nothing to render.
  hasLoadError,
  request,
  onClose,
  onAction,
  actionLoading,
  actionTarget,
}) {
  if (!isOpen) return null;

  const renderActionText = (base, target) =>
    actionLoading && actionTarget === target ? `${base}...` : base;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="mt-6 flex flex-col items-center gap-2 py-10 text-slate-500">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#570033]/30 border-t-[#570033]" />
          <p className="text-sm font-semibold uppercase tracking-[0.3em]">
            Loading request
          </p>
        </div>
      );
    }

    if (hasLoadError) {
      return (
        <div className="mt-6 space-y-2 text-center text-sm text-slate-500">
          <p className="text-base font-semibold text-slate-900">
            Unable to load request
          </p>
        </div>
      );
    }

    if (!request) {
      return (
        <p className="mt-6 text-center text-sm text-slate-500">
          Request details are unavailable.
        </p>
      );
    }

    const initials = request.guestName
      ? request.guestName
          .split(" ")
          .map((part) => (part ? part[0].toUpperCase() : ""))
          .slice(0, 2)
          .join("")
      : "A";

    return (
      <>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6E8F0] text-lg font-semibold text-[#570033]">
            {initials || "A"}
          </div>
          <div>
            <p className="text-md font-semibold text-slate-900">{request.guestName}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <p className="text-[8px] p-1 uppercase tracking-[0.3em] text-slate-400">
              Arrival Date
            </p>
            <div className="space-y-1 p-4 border border-[#F1D7E5] rounded-md">
              <p className=" text-xs font-semibold text-[#8594A6]">
                {request.arrivalDate}
              </p>
            </div>
          </div>
          <div>
            <p className="text-[8px] p-1 uppercase tracking-[0.3em] text-slate-400">
              Departure Date
            </p>
            <div className="space-y-1 p-4 border border-[#F1D7E5] rounded-md">
              <p className=" text-xs font-semibold text-[#8594A6]">
                {request.departureDate}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex flex-row gap-2">
            <div className="border border-1 rounded-full w-11 h-11 flex items-center justify-center bg-[#F6E8F0]">
              <img
                src="/icons/dollar.svg"
                alt="Price Icon"
                className="h-5 w-5"
              />
            </div>
            <div className="flex-col gap-1">
              <p className="text-sm text-[#495666]">Price</p>
              <p className="text-sm text-[#2B333C]">{request.price}</p>
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <div className="border border-1 rounded-full w-11 h-11 flex items-center justify-center bg-[#F6E8F0]">
              <MapPin className="h-4 w-4 text-[#570033]" />
            </div>
            <div className="flex-col gap-1">
              <p className="text-sm text-[#495666]">Pickup</p>
              <p className="text-sm text-[#2B333C]">{request.pickup}</p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#FFF8EE] px-3 py-2 text-sm text-[#7A4C00]">
            <p className="text-[#E38E06]">
              {request.noteLabel || "Additional note"}
            </p>
            <p className="font-bold text-[#E38E06]">{request.note}</p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <button
            type="button"
            className="w-full rounded-lg bg-[#570033] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#6f0044]"
            onClick={() => onAction?.("accepted")}
            disabled={actionLoading}
          >
            {renderActionText("Accept Request", "accepted")}
          </button>
          <button
            type="button"
            className="w-full rounded-lg border border-[#570033] px-4 py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#fdf0f4]"
            onClick={() => onAction?.("declined")}
            disabled={actionLoading}
          >
            {renderActionText("Decline Request", "declined")}
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/60"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-request-title"
          className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-200"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <div>
              <p
                id="service-request-title"
                className="text-lg font-semibold text-[#570033]"
              >
                Service Request
              </p>
            </div>
            <button
              type="button"
              className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-slate-200"
              onClick={onClose}
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>

          {renderBody()}
        </div>
      </div>
    </>
  );
}

export default ServiceRequestModal;
